import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  House, UserPlus, Sparkle, IdentificationCard, Lightning,
  MagnifyingGlass, CalendarBlank, Kanban, Robot, Gear, ArrowRight,
} from '@phosphor-icons/react';

/**
 * /walkthrough — visual, scroll-through guide of the entire
 * SalesCrunchCRM experience (10 screenshots, alternating left/right).
 * Mirrors the MigrationCrunch walkthrough at /migrate/walkthrough.
 * Linked from the homepage hero CTA and the homepage nav.
 */

const STEPS = [
  {
    n: 1,
    icon: House,
    img: '/brand/walkthrough-scrm/step-01-homepage.jpg',
    alt: 'SalesCrunchCRM homepage',
    title: 'Land on the homepage',
    body: (
      <>
        First impression of SalesCrunchCRM. The headline says what it is —{' '}
        <strong>&ldquo;The ACT! 4.0 you remember, rebuilt for 2026&rdquo;</strong>{' '}
        — and the demo card down below shows Crunchy (the built-in AI)
        actually doing work, not just chatting.
        <br />
        <br />
        Hit the big amber <span className="text-amber-600 font-bold">&ldquo;Start your 14-day free trial&rdquo;</span>{' '}
        button when you&apos;re ready. No credit card up front — it&apos;s $0
        today, $30/user/month after the trial, and you can cancel anytime from the
        same dashboard you sign up on.
      </>
    ),
  },
  {
    n: 2,
    icon: UserPlus,
    img: '/brand/walkthrough-scrm/step-02-signup.jpg',
    alt: 'Start your 14-day free trial form',
    title: 'Start your free trial',
    body: (
      <>
        Hit the amber <span className="text-amber-600 font-bold">&ldquo;Start your 14-day free trial&rdquo;</span>{' '}
        button and the form expands right there — no separate sign-up page.
        Just type your name and email, then{' '}
        <span className="text-amber-600 font-bold">&ldquo;Continue to secure checkout&rdquo;</span>{' '}
        drops you on a Stripe page where you enter card details.
        <br />
        <br />
        <strong>You won&apos;t be charged today.</strong> $0 today, first $30/user
        charge lands on day 15, and you can cancel anytime in those 14 days
        from your account settings — no phone call, no email loops.
        <br />
        <br />
        Prefer Google one-click instead? The <strong>Sign in → Register</strong>{' '}
        tab has a &ldquo;Continue with Google&rdquo; option that skips Stripe
        entirely.
      </>
    ),
  },
  {
    n: 3,
    icon: Sparkle,
    img: '/brand/walkthrough-scrm/step-03-first-login.jpg',
    alt: 'First login showing the ACT-style contact card with alarms and whats-new',
    title: 'You\u2019re in. The classic ACT! layout, immediately.',
    body: (
      <>
        No 400-page onboarding. The second you sign in you&apos;re staring at
        the contact card — Business Card on the left, Address center, Status +
        User Fields on the right — exactly where ACT! 4.0 put them in 1998.
        <br />
        <br />
        The orange toolbar across the top (<strong>New / Call / Meeting /
        Note / Email / Save / Delete</strong>) is the same workflow muscle
        memory ACT! veterans already have. The blue sidebar (Contacts,
        Calendar, Tasks, Reports, Templates, Pipeline) is one click away.
        <br />
        <br />
        Two friendly nudges greet you on day one: <strong>Today&apos;s Alarms</strong>{' '}
        in the top-right (anything you scheduled that&apos;s due) and{' '}
        <strong>What&apos;s New</strong> bottom-left (product updates since
        your last login). Dismiss them and the workspace is yours.
      </>
    ),
  },
  {
    n: 4,
    icon: IdentificationCard,
    img: '/brand/walkthrough-scrm/step-04-contact-card.jpg',
    alt: 'Clean three-column contact card',
    title: 'The contact card, exactly where it should be',
    body: (
      <>
        This is the heart of SalesCrunchCRM — and the heart of why ACT! 4.0
        users feel at home in 30 seconds.
        <br />
        <br />
        Three columns, just like 1998:
        <ul className="list-disc pl-5 mt-3 space-y-1.5">
          <li>
            <strong>Business Card</strong> (left) — Contact, Company, Title,
            Phone, Email — the stuff you reach for hundreds of times a day
          </li>
          <li>
            <strong>Address</strong> (center) — full mailing block plus Web
            Site
          </li>
          <li>
            <strong>Status + User Fields</strong> (right) — Lead /
            Prospect / Customer tag, plus 10 renameable User Fields (click
            the pencil to rename them to your industry — &ldquo;Policy #&rdquo;,
            &ldquo;Renewal Date&rdquo;, whatever you need)
          </li>
        </ul>
        <br />
        Below the card: <strong>Activities · History · Notes · Files ·
        Groups · Relationships · Opportunities · Audit Trail</strong> — every
        tab ACT! had, none it didn&apos;t.
      </>
    ),
  },
  {
    n: 5,
    icon: Lightning,
    img: '/brand/walkthrough-scrm/step-05-quick-add.jpg',
    alt: 'Quick Add Contact modal',
    title: 'Add a contact in 5 seconds with Quick Add',
    body: (
      <>
        Phone rings. New prospect. You don&apos;t have time to navigate to a
        form.
        <br />
        <br />
        Hit <strong>Ctrl/Cmd+K</strong> from any screen (or click{' '}
        <strong>Quick Add</strong> in the sidebar) and this lightweight modal
        pops up. First name, last name, company, email, phone, status — the
        absolute minimum to capture the lead before they hang up.
        <br />
        <br />
        Hit <strong>Cmd+Enter</strong> to save and you&apos;re back where you
        were. Need to capture three more from the same call? Hit{' '}
        <strong>&ldquo;Save &amp; Add Another&rdquo;</strong> and the form
        clears for the next one. No page reloads, no losing your place. (And
        if there&apos;s already a Sarah Mitchell in your CRM, it warns you
        before you create a duplicate.)
      </>
    ),
  },
  {
    n: 6,
    icon: MagnifyingGlass,
    img: '/brand/walkthrough-scrm/step-06-lookup.jpg',
    alt: 'Boolean Lookup query builder dialog',
    title: 'Boolean Lookup, the way you remember it',
    body: (
      <>
        If you used ACT! 3.0 or 4.0, this is the screen you&apos;ve been
        looking for since 2014.
        <br />
        <br />
        Hit <strong>Lookup</strong> on the toolbar and you get the same
        Boolean query builder ACT! had — except it runs against 250,000
        contacts in MongoDB instead of grinding a desktop SQL Server to a
        halt.
        <br />
        <br />
        Pick a field (Last Name, Company, State, Tags, Last Activity, any of
        your 10 User Fields), pick an operator (<strong>equals · contains
        · starts with · is empty · greater than · less than · between ·
        never</strong>), type a value, and click{' '}
        <strong>Add Another Condition</strong> to stack them with AND/OR
        logic.
        <br />
        <br />
        <em>&ldquo;Everyone in Texas with &lsquo;Tech&rsquo; in their
        company who I haven&apos;t called in 90 days.&rdquo;</em> Three
        conditions. Two seconds.
      </>
    ),
  },
  {
    n: 7,
    icon: CalendarBlank,
    img: '/brand/walkthrough-scrm/step-07-activity.jpg',
    alt: 'Log Activity dialog showing the Meeting tab',
    title: 'Log a call, meeting, note, or email in 4 clicks',
    body: (
      <>
        Toolbar buttons: <strong>Call · Email · Meeting · Note</strong>.
        Click any of them and this Log Activity dialog drops in.
        <br />
        <br />
        Pick <strong>Date · Time · Priority</strong> (Low/Medium/High), set
        a <strong>Reminder</strong> if you want an alarm to pop up later,
        type a quick <strong>Details</strong> note, hit Save. Done. The
        activity logs to this contact&apos;s history with a timestamp and
        shows up on your Task List and Calendar.
        <br />
        <br />
        Need it to repeat? Check <strong>&ldquo;Make recurring&rdquo;</strong>{' '}
        — daily, weekly, bi-weekly, monthly, or yearly, up to 52 instances.
        Set your standing Tuesday review call once, it&apos;s on your task
        list forever.
        <br />
        <br />
        Up in the top-right corner, <strong>Today&apos;s Alarms</strong>{' '}
        rolls in whenever something&apos;s due — drag the panel anywhere on
        screen, click an alarm to jump to that contact, snooze, complete, or
        reschedule.
      </>
    ),
  },
  {
    n: 8,
    icon: Kanban,
    img: '/brand/walkthrough-scrm/step-08-pipeline.jpg',
    alt: 'Sales Pipeline Kanban with six stages',
    title: 'Watch every dollar move through the pipeline',
    body: (
      <>
        Click <strong>Opportunities</strong> in the sidebar and you land in
        the Sales Pipeline — drag-and-drop Kanban with every active deal
        grouped by stage.
        <br />
        <br />
        Six columns, in classic sales order: <strong>Lead → Qualified →
        Proposal → Negotiation → Won / Lost</strong>. The header at the top
        of each column shows the deal count and the total dollar value — so
        at a glance you can see <em>&ldquo;$307K in proposal, $261K
        negotiating&rdquo;</em> without opening a report.
        <br />
        <br />
        Each card carries the deal name, amount, and expected close date.
        Grab one and drag it from Proposal to Negotiation — the totals at
        the top recalculate live. Hit <strong>+ Add</strong> at the bottom
        of any column to drop a new deal in, or{' '}
        <strong>+ Add Deal</strong> top-right to start one from the contact
        card.
        <br />
        <br />
        The page header is honest about your whole book:{' '}
        <strong>&ldquo;22 deals · $1.2M in pipeline.&rdquo;</strong> No
        forecasting models, no probability juggling — just what&apos;s
        actually live.
      </>
    ),
  },
  {
    n: 9,
    icon: Robot,
    img: '/brand/walkthrough-scrm/step-09-crunchy.jpg',
    alt: 'Crunchy AI drawer answering a stale-contacts question',
    title: 'Meet Crunchy, your built-in AI sidekick',
    body: (
      <>
        Bottom-right corner of every page: the{' '}
        <strong>&ldquo;Ask Crunchy&rdquo;</strong> button. Click it (or hit{' '}
        <strong>Cmd/Ctrl+/</strong>) and Crunchy slides in — a draggable,
        dismissable chat drawer that doesn&apos;t take you away from the
        page you&apos;re working on.
        <br />
        <br />
        Crunchy isn&apos;t a bolted-on chatbot. He&apos;s been trained on
        SalesCrunchCRM specifically — he knows where every feature lives,
        and he has <em>hands</em>. Ask him <em>&ldquo;Show me everyone in
        Indiana I haven&apos;t called in 60 days&rdquo;</em> and he actually
        runs the lookup against your data and answers —{' '}
        <em>&ldquo;Good news, you&apos;re on top of it&rdquo;</em> —
        instead of just describing how you could do it yourself.
        <br />
        <br />
        Other tricks he&apos;s good at:
        <ul className="list-disc pl-5 mt-2 space-y-1.5">
          <li><strong>Search + filter</strong> — &ldquo;count my customers in Texas&rdquo;</li>
          <li><strong>Schedule activities</strong> — &ldquo;log a 30-min call with Bob Smith tomorrow at 2pm&rdquo;</li>
          <li><strong>Bulk emails with approval</strong> — &ldquo;write a touch-base note to every lead I haven&apos;t called in 90 days&rdquo;</li>
          <li><strong>Account questions</strong> — subscription status, billing portal link</li>
        </ul>
        <br />
        You get <strong>150 messages a month included free</strong>, the
        counter is right there in the header. When he hits a limit of what
        he can help with, the <strong>&ldquo;Need a human?&rdquo;</strong>{' '}
        button auto-sends your full transcript to support — they show up
        already up to speed.
      </>
    ),
  },
  {
    n: 10,
    icon: Gear,
    img: '/brand/walkthrough-scrm/step-10-settings.jpg',
    alt: 'Settings page showing Profile, Email, and User Management sections',
    title: 'Settings: make the workspace yours',
    body: (
      <>
        Click <strong>Settings</strong> in the sidebar — everything you need
        to customize SalesCrunchCRM lives here, in one scroll.
        <br />
        <br />
        <strong>Your Profile</strong> — name, avatar, email, role badge.
        <br />
        <br />
        <strong>Email — Connect Gmail</strong> — link your Gmail in one
        click and CRM emails go out through <em>your</em> inbox, not a
        generic sender. Better deliverability, no spam-folder issues.
        <br />
        <br />
        <strong>User Management</strong> — invite teammates, set each to{' '}
        <strong>Admin / Manager / User</strong>. Roles take effect
        instantly.
        <br />
        <br />
        <strong>Custom User Fields</strong> — rename the 10 User Fields on
        the contact card to match your industry (Policy #, Premium, Renewal
        Date — whatever). Same workflow ACT! always had, instant cloud
        sync.
        <br />
        <br />
        <strong>Workspace Backup</strong> — one-click export of every
        custom field, drop-down, and template definition as a JSON file.
        Restore the whole admin setup to any new install in 30 seconds.
        <br />
        <br />
        <strong>Subscription &amp; billing</strong> are handled through
        Stripe&apos;s hosted billing portal — update card, see invoices,
        cancel anytime. Ask Crunchy <em>&ldquo;open my billing
        portal&rdquo;</em> and he hands you the link, no menu hunting.
      </>
    ),
  },
];

export default function SalesCrunchWalkthroughPage() {
  useEffect(() => {
    document.title = 'How SalesCrunchCRM works — the full walkthrough';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="scrm-walkthrough-page">
      {/* Top bar — mirrors the homepage style */}
      <header className="border-b border-slate-200 px-6 py-4 sticky top-0 bg-white/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="scrm-walkthrough-nav-logo"
          >
            <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0B5394] text-white text-xs font-black rounded">
              SC
            </span>
            <span className="text-lg font-black">
              SalesCrunch<span className="text-[#0B5394]">CRM</span>
            </span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link
              to="/"
              className="hidden md:inline text-slate-600 hover:text-[#0B5394] transition-colors"
              data-testid="scrm-walkthrough-nav-home"
            >
              Home
            </Link>
            <Link
              to="/#pricing"
              className="hidden md:inline text-slate-600 hover:text-[#0B5394] transition-colors"
              data-testid="scrm-walkthrough-nav-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/"
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-4 py-2 rounded transition-colors"
              data-testid="scrm-walkthrough-nav-cta"
            >
              Start free trial →
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 text-center max-w-3xl mx-auto">
        <p
          className="text-[#0B5394] text-xs font-bold uppercase tracking-widest mb-3"
          data-testid="scrm-walkthrough-tagline"
        >
          A full tour, no salesy nonsense
        </p>
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6"
          data-testid="scrm-walkthrough-headline"
        >
          Here&apos;s exactly what you get
          <br />
          <span className="text-[#0B5394]">from sign-up to daily workflow.</span>
        </h1>
        <p className="text-lg text-slate-600 leading-relaxed">
          Ten screens, in order. About a 3-minute read — and roughly the same
          time it takes to actually sign up and start working. If anything
          here&apos;s unclear,{' '}
          <a
            href="mailto:chip@salescrunchcrm.com"
            className="text-[#0B5394] underline hover:text-[#073763] font-semibold"
            data-testid="scrm-walkthrough-email-chip"
          >
            email Chip
          </a>
          .
        </p>

        {/* Jump nav pills */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
          {STEPS.map((s) => (
            <a
              key={s.n}
              href={`#step-${s.n}`}
              className="text-xs font-bold bg-white hover:bg-[#0B5394] border border-slate-200 hover:border-[#0B5394] text-slate-600 hover:text-white px-3 py-1.5 rounded transition-colors"
              data-testid={`scrm-walkthrough-jump-${s.n}`}
            >
              Step {s.n}
            </a>
          ))}
        </div>
      </section>

      {/* Steps — alternating left/right scroll */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto space-y-24">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const flip = idx % 2 === 1;
            return (
              <article
                key={s.n}
                id={`step-${s.n}`}
                className="scroll-mt-24 grid md:grid-cols-2 gap-10 lg:gap-16 items-center"
                data-testid={`scrm-walkthrough-step-${s.n}`}
              >
                {/* Screenshot */}
                <div
                  className={`${flip ? 'md:order-2' : ''} rounded-xl overflow-hidden border border-slate-200 bg-white shadow-2xl shadow-slate-300/40`}
                >
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </div>
                {/* Copy */}
                <div className={`${flip ? 'md:order-1' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0B5394] text-white font-black text-base"
                      data-testid={`scrm-walkthrough-step-num-${s.n}`}
                    >
                      {s.n}
                    </span>
                    <Icon size={22} weight="bold" className="text-[#F39C12]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-4 text-slate-900">
                    {s.title}
                  </h2>
                  <div className="text-base text-slate-700 leading-relaxed">
                    {s.body}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="px-6 py-16 border-t border-slate-200 bg-white"
        data-testid="scrm-walkthrough-cta-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-slate-900">
            That&apos;s the whole thing.
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            No phone calls, no implementation specialist, no $5,000 invoice.
            Sign up, click around, and only pay if you&apos;re still using it
            on day 15.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded text-base inline-flex items-center gap-2 transition-colors"
              data-testid="scrm-walkthrough-cta-trial"
            >
              <Lightning size={20} weight="bold" />
              Start your 14-day free trial
            </Link>
            <a
              href="mailto:chip@salescrunchcrm.com"
              className="text-sm text-slate-600 hover:text-[#0B5394] inline-flex items-center gap-2 px-4 py-2 transition-colors"
              data-testid="scrm-walkthrough-cta-email"
            >
              Or ask Chip a question first →
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            <ArrowRight size={11} weight="bold" className="inline mr-1" />
            $0 today. $30/user/month after trial. Cancel anytime.
          </p>
          <p className="text-sm text-slate-500 mt-8 pt-6 border-t border-slate-200 max-w-xl mx-auto">
            Coming from another CRM with a messy export?{' '}
            <Link
              to="/migrate/walkthrough"
              className="text-[#0B5394] hover:text-[#073763] underline font-semibold"
              data-testid="scrm-walkthrough-cross-link"
            >
              See how MigrationCrunch cleans it up →
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
